import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as agentApi from '../../services/agent-api.js';

const MAX_SIZE_BYTES = 500 * 1024; // 500KB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

@customElement('cp-agent-avatar-upload')
export class CpAgentAvatarUpload extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) agentId = 0;
  @property({ type: String }) agentName = '';
  @property({ type: String }) agentColor = '#6366f1';
  @property({ type: Boolean }) hasAvatar = false;

  @state() private _hasAvatarLocal: boolean | null = null;
  @state() private _preview: string | null = null;
  @state() private _uploading = false;
  @state() private _removing = false;
  @state() private _error: string | null = null;
  @state() private _dragOver = false;

  private _fileInput: HTMLInputElement | null = null;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('agentId')) {
      this._preview = null;
      this._error = null;
      this._hasAvatarLocal = null;
    }
  }

  private get _effectiveHasAvatar(): boolean {
    return this._hasAvatarLocal ?? this.hasAvatar;
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = true;
  }

  private _onDragLeave(): void {
    this._dragOver = false;
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = false;
    const file = e.dataTransfer?.files[0];
    if (file) {
      void this._processFile(file);
    }
  }

  private _onClickUpload(): void {
    if (!this._fileInput) {
      this._fileInput = document.createElement('input');
      this._fileInput.type = 'file';
      this._fileInput.accept = ALLOWED_TYPES.join(',');
      this._fileInput.addEventListener('change', () => {
        const file = this._fileInput?.files?.[0];
        if (file) {
          void this._processFile(file);
        }
      });
    }
    this._fileInput.value = '';
    this._fileInput.click();
  }

  private async _processFile(file: File): Promise<void> {
    this._error = null;

    if (!ALLOWED_TYPES.includes(file.type)) {
      this._error = 'Nur PNG, JPG und WebP Bilder sind erlaubt';
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      this._error = 'Bild darf maximal 500KB gross sein';
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => {
      this._preview = reader.result as string;
    };
    reader.readAsDataURL(file);

    // Upload
    this._uploading = true;
    try {
      await agentApi.uploadAvatar(this.agentId, file);
      this._hasAvatarLocal = true;
      this.dispatchEvent(new CustomEvent('avatar-uploaded', {
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Upload fehlgeschlagen';
      this._preview = null;
    } finally {
      this._uploading = false;
    }
  }

  private async _removeAvatar(): Promise<void> {
    this._removing = true;
    this._error = null;
    try {
      await agentApi.deleteAvatar(this.agentId);
      this._hasAvatarLocal = false;
      this._preview = null;
      this.dispatchEvent(new CustomEvent('avatar-removed', {
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Entfernen fehlgeschlagen';
    } finally {
      this._removing = false;
    }
  }

  private _getAvatarDisplay() {
    if (this._preview) {
      return html`<img class="cp-agent-avatar-upload__img" src=${this._preview} alt="${this.agentName}" />`;
    }
    if (this._effectiveHasAvatar) {
      return html`<img class="cp-agent-avatar-upload__img" src=${agentApi.getAvatarUrl(this.agentId)} alt="${this.agentName}" />`;
    }
    return html`
      <div class="cp-agent-avatar-upload__initials" style="background-color: ${this.agentColor}">
        ${this.agentName.charAt(0).toUpperCase()}
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-agent-avatar-upload">
        <div class="cp-agent-avatar-upload__preview-area">
          <div
            class="cp-agent-avatar-upload__dropzone ${this._dragOver ? 'cp-agent-avatar-upload__dropzone--active' : ''}"
            @dragover=${(e: DragEvent) => this._onDragOver(e)}
            @dragleave=${() => this._onDragLeave()}
            @drop=${(e: DragEvent) => this._onDrop(e)}
            @click=${() => this._onClickUpload()}
            role="button"
            tabindex="0"
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._onClickUpload()}
          >
            ${this._getAvatarDisplay()}
            <div class="cp-agent-avatar-upload__overlay-hint">
              ${this._uploading ? 'Uploading...' : 'Bild hochladen'}
            </div>
          </div>
        </div>

        <div class="cp-agent-avatar-upload__actions">
          <button
            class="cp-agent-avatar-upload__btn"
            @click=${() => this._onClickUpload()}
            ?disabled=${this._uploading}
          >${this._uploading ? 'Hochladen...' : 'Bild waehlen'}</button>

          ${this._effectiveHasAvatar ? html`
            <button
              class="cp-agent-avatar-upload__btn cp-agent-avatar-upload__btn--remove"
              @click=${() => void this._removeAvatar()}
              ?disabled=${this._removing}
            >${this._removing ? 'Entfernt...' : 'Entfernen'}</button>
          ` : nothing}
        </div>

        <p class="cp-agent-avatar-upload__hint">PNG, JPG, WebP. Max 500KB.</p>

        ${this._error ? html`
          <div class="cp-agent-avatar-upload__error">${this._error}</div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-avatar-upload': CpAgentAvatarUpload;
  }
}
